import React, { useEffect, useState } from "react";
import { Col, Container, Modal, Pagination, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  ActionIconContainer,
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";
import {
  CheckBoxInput,
  FormItem,
} from "../../../shared/components/form/FormElements";
import { formInputStyles } from "../../../shared/constants/styles";
import styled from "styled-components";
import {
  colorBorder,
  colorLightGray,
  colorRowHover,
  scrollbarStyles,
} from "../../../utils/palette";
import { post } from "../../../api/getUser";
import PartsFormPopup from "../Parts/PartsFormPopup";
import { TableSpinner } from "../../../shared/components/table/components/ReactTableConstructor";
import { useAuth } from "../../../contexts/AuthContext";
import Error from "@/shared/components/form/Error";
import {
  PaginationInfo,
  PaginationWrap,
} from "../../../shared/components/table/components/ReactTablePagination";
import useDebounce from "../../../shared/custom-hooks/useDebounce";
import { postApi } from "../../../api/getUser";
import SubscribePopup from "../../../shared/components/SubscribePopup";
import { de } from "date-fns/locale";

const pageSize = 10;

const SelectSuggestedPartsPopup = ({
  showPopup,
  setShowPopup,
  onPartsSubmitted,
  alreadySelectedParts = [],
}) => {
  const { t } = useTranslation("common");
  const { reload, setReload, tag } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [allParts, setAllParts] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageIndexArr, setPageIndexArr] = useState([]);
  const [sorting, setSorting] = useState("desc");
  const [filterText, setFilterText] = useState("");
  const debounceFilterText = useDebounce(filterText, 500);

  const [selectedParts, setSelectedParts] = useState([]);
  const [errMsg, setErrMsg] = useState("");

  const [showPartsForm, setShowPartsForm] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [subscribePopupErrMsg, setSubscribePopupErrMsg] = useState("");

  // Pre-populate selection when popup opens
  useEffect(() => {
    if (showPopup) {
      setSelectedParts(alreadySelectedParts || []);
      setFilterText("");
      setPageIndex(0);
    }
  }, [showPopup]);

  const fetchParts = async (index = 0) => {
    setIsLoading(true);
    const apiData = {
      querystring: "get_parts_for_service_task",
      search: debounceFilterText ? debounceFilterText.toLowerCase() : "",
      order: sorting,
      page: index + 1,
      limit: pageSize,
    };
    const res = await postApi(apiData, "inventory");
    if (res?.success) {
      setAllParts(res.inventory_data || []);
      setTotalRecords(res.recordsTotal || 0);
      const noOfPages = Math.ceil((res.recordsTotal || 0) / pageSize);
      setPageIndexArr([...Array(noOfPages).keys()]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (showPopup) {
      fetchParts(0);
      setPageIndex(0);
    }
  }, [debounceFilterText, sorting, showPopup]);

  useEffect(() => {
    if (reload === "parts") {
      setReload("");
      fetchParts(pageIndex);
    }
  }, [reload]);

  const isSelected = (part) =>
    selectedParts.some((p) => p.part_id === part.part_id);

  const togglePart = (part) => {
    setErrMsg("");
    setSelectedParts((prev) => {
      if (prev.some((p) => p.part_id === part.part_id)) {
        return prev.filter((p) => p.part_id !== part.part_id);
      }
      return [
        ...prev,
        {
          part_id: part.part_id,
          part_name: part.part_name,
          part_number: part.part_number || "",
        },
      ];
    });
  };

  const handleClose = () => {
    setShowPopup(false);
  };

  const handleAddPart = async () => {
    if (tag === "free-user") {
      const res = await postApi(
        { querystring: "get_parts_count" },
        "inventory",
      );
      if (res?.success && res?.inventory_data >= 3) {
        setSubscribePopupErrMsg(t("no_of_parts_limit_err"));
        setShowSubscribePopup(true);
        return;
      }
    }
    if (tag === "essential-user") {
      setSubscribePopupErrMsg(t("subscribe_to_advanced_msg"));
      setShowSubscribePopup(true);
      return;
    }
    setShowPartsForm(true);
  };

  const handleSubmit = () => {
    // if (selectedParts.length === 0) {
    //   setErrMsg(t("one_batch_reqd_err"));
    //   return;
    // }
    onPartsSubmitted(selectedParts);
    setShowPopup(false);
  };

  const selectedCount = selectedParts.length;

  return (
    <>
      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={subscribePopupErrMsg}
        />
      )}
      {showPartsForm && (
        <PartsFormPopup
          showPopup={showPartsForm}
          setShowPopup={setShowPartsForm}
          mode="add-part"
          rowData={{}}
          setSnackbarMsg={() => {}}
        />
      )}
      <Modal
        show={showPopup && !showPartsForm}
        size="lg"
        backdropClassName="custom-backdrop"
      >
        <Modal.Header>
          <h4>{t("select_suggested_parts_title")}</h4>
          <div className="d-flex align-items-center gap-2">
            <AddFormButton type="button" onClick={handleAddPart}>
              {t("add_part")}
            </AddFormButton>
            <ActionIconContainer onClick={handleClose}>
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
        </Modal.Header>
        <Modal.Body className="m-0 p-0">
          <Container className="mt-3 mb-2 px-3">
            <FormItem>
              <input
                type="text"
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPageIndex(0);
                }}
                style={formInputStyles}
                placeholder={t("search_parts")}
              />
            </FormItem>
          </Container>
          <Container className="m-0 p-0">
            <ColumnHeader>
              <CheckboxCell />
              <PartNameCell style={{ fontWeight: 300, fontSize: "12px" }}>
                {t("part_name")}
                <i
                  className={`fi fi-rr-angle-small-${sorting === "desc" ? "down" : "up"}`}
                  style={{
                    cursor: "pointer",
                    position: "relative",
                    top: "2px",
                    marginLeft: "4px",
                  }}
                  onClick={() => {
                    setSorting((prev) => (prev === "desc" ? "asc" : "desc"));
                    setPageIndex(0);
                  }}
                />
              </PartNameCell>
              <PartNumberCell style={{ fontWeight: 300, fontSize: "12px" }}>
                {t("part_no")}
              </PartNumberCell>
            </ColumnHeader>
            <RowContainer $height={`${window.innerHeight - 380}px`}>
              {isLoading ? (
                <div className="text-center py-4">
                  <TableSpinner animation="border" />
                </div>
              ) : allParts.length === 0 ? (
                <EmptyRow>
                  <p>{t("no_records")}</p>
                </EmptyRow>
              ) : (
                allParts.map((part) => {
                  const selected = isSelected(part);
                  return (
                    <PartRow
                      key={part.part_id}
                      $selected={selected}
                      onClick={() => togglePart(part)}
                    >
                      <CheckboxCell>
                        <CheckBoxInput
                          type="checkbox"
                          checked={selected}
                          readOnly
                        />
                      </CheckboxCell>
                      <PartNameCell>{part.part_name}</PartNameCell>
                      <PartNumberCell>{part.part_number || "—"}</PartNumberCell>
                    </PartRow>
                  );
                })
              )}
            </RowContainer>
            {errMsg && (
              <div className="mx-3 mt-1">
                <Error error={errMsg} />
              </div>
            )}
            <PaginationWrap className="my-2 mx-2 p-0">
              <Pagination>
                <Pagination.First
                  disabled={pageIndex === 0}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    setPageIndex(0);
                    await fetchParts(0);
                  }}
                >
                  <i className="fi fi-rr-angle-double-small-left" />
                </Pagination.First>
                <Pagination.Prev
                  disabled={pageIndex === 0}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    const p = pageIndex - 1;
                    setPageIndex(p);
                    await fetchParts(p);
                  }}
                >
                  <i className="fi fi-rr-angle-small-left" />
                </Pagination.Prev>
                {pageIndexArr.map((i) => (
                  <Pagination.Item
                    key={i}
                    active={pageIndex === i}
                    onClick={async () => {
                      setPageIndex(i);
                      await fetchParts(i);
                    }}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={pageIndex === pageIndexArr.length - 1}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    const p = pageIndex + 1;
                    setPageIndex(p);
                    await fetchParts(p);
                  }}
                >
                  <i className="fi fi-rr-angle-small-right" />
                </Pagination.Next>
                <Pagination.Last
                  disabled={pageIndex === pageIndexArr.length - 1}
                  className="pagination__item--arrow"
                  onClick={async () => {
                    const p = pageIndexArr.length - 1;
                    setPageIndex(p);
                    await fetchParts(p);
                  }}
                >
                  <i className="fi fi-rr-angle-double-small-right" />
                </Pagination.Last>
              </Pagination>
              <PaginationInfo>
                {t("showing_from_to_no_of_rows", {
                  from: totalRecords > 0 ? pageSize * pageIndex + 1 : 0,
                  to: Math.min(pageSize * (pageIndex + 1), totalRecords),
                  noOfRows: totalRecords,
                })}
              </PaginationInfo>
            </PaginationWrap>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <p style={{ flex: 1, margin: 0, fontSize: "13px", color: "#888" }}>
            {t("select_parts_to_suggest_msg")}
          </p>

          <div className="d-flex gap-2">
            <FilterButton type="button" onClick={handleClose}>
              {t("cancel")}
            </FilterButton>
            <AddFormButton type="button" onClick={handleSubmit} disabled={selectedCount === 0}>
              {selectedCount > 0
                ? `${t("submit")} - ${t("count_parts", { count: selectedCount })}`
                : t("submit")}
            </AddFormButton>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SelectSuggestedPartsPopup;

// ─── Styled Components ────────────────────────────────────────────────────────

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-bottom: 1px solid ${colorLightGray};
  border-top: 1px solid ${colorLightGray};
  background: #f8f9fa;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const PartRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid ${colorLightGray};
  background: ${(p) => (p.$selected ? "#eef3ff" : "white")};
  &:hover {
    background: ${(p) => (p.$selected ? "#e4ecff" : colorRowHover)};
  }
`;

const CheckboxCell = styled.div`
  width: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const PartNameCell = styled.div`
  flex: 1;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PartNumberCell = styled.div`
  width: 200px;
  flex-shrink: 0;
  font-size: 13px;
`;

export const RowContainer = styled(Container)`
  overflow: auto;
  height: ${(p) => p.$height};
  margin: 0;
  padding: 0;
  ${scrollbarStyles}
`;

const EmptyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  font-size: 13px;
  color: #888;
`;
